import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { patchSearchParams, TSearchParamsPatch } from 'app/helpers/patchSearchParams';
import { parseQueryParamInt } from 'app/helpers/parseQueryParamInt';
import { parseQueryParamOrder, TOrderType } from 'app/helpers/parseQueryParamOrder';

type TDefaults = {
  pageSize: number;
  orderBy: string;
  orderType: TOrderType;
};

type TOptions = {
  defaults: TDefaults;
  replaceSearch?: boolean;
};

type TUpdateParamsOptions = {
  resetPage?: boolean;
  replace?: boolean;
};

export const useListQueryParams = ({ defaults, replaceSearch = false }: TOptions) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = useMemo(() => parseQueryParamInt(searchParams.get('page'), { fallback: 1, min: 1 }), [searchParams]);
  const pageSize = useMemo(
    () => parseQueryParamInt(searchParams.get('pageSize'), { fallback: defaults.pageSize, min: 1 }),
    [defaults.pageSize, searchParams],
  );
  const search = useMemo(() => searchParams.get('search') || '', [searchParams]);

  const { orderBy, orderType } = useMemo(() => parseQueryParamOrder(searchParams, defaults), [defaults, searchParams]);

  const updateParams = useCallback(
    (patch: TSearchParamsPatch, options?: TUpdateParamsOptions) => {
      setSearchParams(
        (prev) => {
          const nextPatch: TSearchParamsPatch = options?.resetPage ? { ...patch, page: '1' } : patch;
          const next = patchSearchParams(prev, nextPatch);
          const resolvedPage = parseQueryParamInt(next.get('page'), { fallback: 1, min: 1 });
          if (resolvedPage === 1) {
            next.delete('page');
          }
          return next;
        },
        { replace: options?.replace ?? false },
      );
    },
    [setSearchParams],
  );

  const setPage = useCallback(
    (nextPage: number) => {
      updateParams({ page: String(Math.max(1, nextPage)) });
    },
    [updateParams],
  );

  const setPageSize = useCallback(
    (nextPageSize: number) => {
      const normalized = Math.max(1, nextPageSize);
      updateParams(
        {
          pageSize: normalized === defaults.pageSize ? null : String(normalized),
        },
        { resetPage: true },
      );
    },
    [defaults.pageSize, updateParams],
  );

  const setSearch = useCallback(
    (nextSearch: string) => {
      updateParams({ search: nextSearch || null }, { resetPage: true, replace: replaceSearch });
    },
    [replaceSearch, updateParams],
  );

  const setSort = useCallback(
    (nextOrderBy: string, nextOrderType: TOrderType) => {
      updateParams(
        {
          orderBy: nextOrderBy === defaults.orderBy ? null : nextOrderBy,
          orderType: nextOrderType === defaults.orderType ? null : nextOrderType,
        },
        { resetPage: true },
      );
    },
    [defaults.orderBy, defaults.orderType, updateParams],
  );

  const toggleSort = useCallback(
    (field: string) => {
      const nextOrderType: TOrderType = orderBy !== field ? 'asc' : orderType === 'asc' ? 'desc' : 'asc';
      setSort(field, nextOrderType);
    },
    [orderBy, orderType, setSort],
  );

  return {
    searchParams,
    page,
    pageSize,
    search,
    orderBy,
    orderType,
    updateParams,
    setPage,
    setPageSize,
    setSearch,
    setSort,
    toggleSort,
  };
};
